import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import DossierStatusBadge from './DossierStatusBadge';

const formatCurrency = (n) =>
  n ? new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(n) : '—';

const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'nouveau', label: 'Nouveaux' },
  { value: 'instruction_en_cours', label: 'En instruction' },
  { value: 'dossier_valide', label: 'Validés' },
  { value: 'projet_acte_depose', label: "Acte déposé" },
  { value: 'transaction_cloturee', label: 'Clôturés' },
];

const DossierList = ({ dossiers, loading, onSelectDossier, selectedDossierId }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = dossiers?.filter(d => {
    const matchStatus = filter === 'all' || d?.status === filter;
    const matchSearch = !search ||
      d?.reference?.toLowerCase()?.includes(search?.toLowerCase()) ||
      d?.property_title?.toLowerCase()?.includes(search?.toLowerCase()) ||
      d?.property_address?.toLowerCase()?.includes(search?.toLowerCase());
    return matchStatus && matchSearch;
  });

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-white rounded-2xl border border-border/50 p-4 animate-pulse">
            <div className="h-4 bg-slate-100 rounded w-1/3 mb-2" />
            <div className="h-3 bg-slate-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="Search" size={15} className="text-text-secondary" />
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          placeholder="Rechercher par référence ou bien..."
          className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl text-ios-callout font-system text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all bg-white"
        />
      </div>

      {/* Status filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {STATUS_FILTERS?.map(f => (
          <button
            key={f?.value}
            onClick={() => setFilter(f?.value)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-ios-caption1 font-system font-body-medium transition-all ${
              filter === f?.value
                ? 'bg-brand-earth text-white shadow-ios-light'
                : 'bg-white border border-border text-text-secondary hover:border-brand-gold/50'
            }`}>
            {f?.label}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border/50">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Icon name="FolderOpen" size={22} className="text-text-secondary" />
          </div>
          <p className="text-ios-callout font-system font-body-medium text-text-secondary">Aucun dossier trouvé</p>
          <p className="text-ios-caption1 text-text-secondary mt-1">Modifiez vos filtres ou attendez l'attribution d'un dossier.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered?.map(dossier => (
            <button
              key={dossier?.id}
              onClick={() => onSelectDossier?.(dossier)}
              className={`w-full text-left bg-white rounded-2xl border transition-all duration-200 p-4 hover:shadow-ios-light hover:border-brand-gold/40 ${
                selectedDossierId === dossier?.id ? 'border-brand-earth shadow-ios-light ring-1 ring-brand-earth/20' : 'border-border/50'
              }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-ios-caption1 font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2 py-0.5 rounded-lg">
                      {dossier?.reference}
                    </span>
                    {dossier?.is_verified_badge && (
                      <span className="flex items-center space-x-1 text-[10px] font-system font-body-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                        <Icon name="BadgeCheck" size={10} />
                        <span>Vérifié</span>
                      </span>
                    )}
                  </div>
                  <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate">
                    {dossier?.property_title || 'Bien non renseigné'}
                  </p>
                  <p className="text-ios-caption1 text-text-secondary mt-0.5 truncate">
                    {dossier?.property_address || '—'}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-ios-caption1 text-text-secondary font-system">
                      {formatCurrency(dossier?.transaction_amount)}
                    </span>
                    <span className="text-text-secondary/40">·</span>
                    <span className="text-ios-caption1 text-text-secondary font-system">
                      {formatDate(dossier?.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <DossierStatusBadge status={dossier?.status} size="sm" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DossierList;
